import GameObject from '../../../GameObject/GameObject';
import Game from '../../../Game';
import BasePopupHandler from './BasePopupHandler';
import UIItem, { IImageData } from '../../../Prototype/UI/UIItem';
import { TimerActions } from '../../../State/Timer/TimerActions';
import { TimerType } from '../../Timer/TimerTypes';
import BaseUISystem from '../BaseUISystem';
import UIImageComponent from '../../../Component/UI/Image';
import ElementComponent from '../../../Component/UI/Element';
import { GameActions } from '../../../State/Game/GameActions';
import UITextComponent from '../../../Component/UI/Text';
import Element from '../../../Prototype/UI/Element';
import { FBTrackingEvent } from '../../../Utility/Tracking/FBTrackingEvent';
import { ContextType } from '../../../State/Game/ContextType';
import { getCropUnlockShare } from '../../../Constants/AvatarsAndCrops';
import { PopupID } from '../../../State/UI/PopupID';
import { IUIState } from '../../../State/UI/IUIState';
import { IUIButtonData } from 'Prototype/UI/BaseElements/UIButton';
import { Platform } from 'Utility/Platform';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import LeaderboardHelper from '../LeaderboardHelper';
import SpriteComponent from 'Component/Render/Sprite';

export default class CropUnlockedShareHandler extends BasePopupHandler {
    private field: string;
    private buttons: IUIButtonData[] = [this.continueBtnParams, this.closeBtnParams];

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.CROP_UNLOCKED_SHARE_MENU, uiSystem);
    }

    protected onCreate() {
        this.createPopup();
    }

    protected createPopup(params?: any): void {
        this.clipShow = 'ShowCropUnlockShare';

        this.underlayParams.cacheKey = 'splash_harvest';
        this.submitBtnParams.text.textLocalizationKey = 'Share';
        this.submitBtnParams.text.style = Game.style.getStyle('popup.dark_blue_btn');
        this.submitBtnParams.button.cacheKey = 'button_medium_blue';
        this.submitBtnParams.background.layout.offsets = [200, -4, 200, -4];
        this.backgroundParams.layout.constrains = {
            heightMax: 740,
            heightMin: 740,
            widthMax: 1250,
            widthMin: 1250,
        };
        this.backgroundParams.cacheKey = 'blank';
        this.closeBtnParams.button.cacheKey = 'button_close';
        this.closeBtnParams.button.layout.anchors = [0.93, 0.12, 0.93, 0.12];

        this.configureShareButtonForNative();
        super.createPopup({
            buttons: this.buttons,
            title: null,
        });
        const playerIcon: GameObject = UIItem.createImage({
            name: 'player_avatar',
            cacheKey: 'avatar3',
            layout: {
                pivot: [0.5, 0.5],
                offsets: [-5, -42, -5, -42], //54
                scale: [1, 1],
                constrains: {
                    widthMax: 154,
                    widthMin: 154,
                    heightMax: 154,
                    heightMin: 154,
                },
            },
        });

        this.base.addChild(playerIcon);

        const newCropText: GameObject = GameObject.Instantiate(Element, {
            type: 'group',
            name: 'new_crop_txt',
            layout: {
                anchors: [0, 0, 0, 0],
                pivot: [0.5, 0.5],
                offsets: [-750, -160, -750, -160],
            },
            components: [
                {
                    type: 'text',
                    data: {
                        textLocalizationKey: 'NewCropUnlocked',
                        style: Game.style.getStyle('CropUnlockedShare'),
                        align: 'center',
                    },
                },
            ],
        });
        this.background.addChild(newCropText);
    }

    public *show(state: IUIState): IterableIterator<number> {
        const iterator = super.show(state);
        this.system.coroutines.push({ iterator });
    }

    public onShow(params?: { id: string }) {
        super.onShow(params);
        const state = Game.store.getState();
        const cropId = WorldDataHelper.getFieldNameFromId(params.id);
        this.field = cropId;
        const fieldData = WorldDataHelper.getFieldDataByCropId(cropId);

        const backgroundImageComponent: UIImageComponent = this.background.getComponent(UIImageComponent);
        this.background.find('new_crop_txt').getComponentsInChildren(UITextComponent)[0].text = Game.locale
            .getText('NewCropUnlocked')
            .replace('[x]', Game.locale.getText(cropId));

        const base: GameObject = this.element.find('baseLayer');
        const playerAvatar: GameObject = base.find('player_avatar');
        const playerAvatarImageComponent = playerAvatar.getComponent(UIImageComponent);
        LeaderboardHelper.setPlayerAvatar(
            'player_avatar',
            Game.sdk.getAvatar(),
            playerAvatarImageComponent,
            154,
            () => {
                playerAvatar.getComponent(ElementComponent).anchors = [...fieldData.shares.crop.offsets];
            }
        );

        backgroundImageComponent.texture = getCropUnlockShare(cropId).key;
    }

    protected toggleInteractables(value: boolean): void {
        // handles interactables itself
    }

    protected onSubmit(): void {
        super.onSubmit();
        Game.sdk.gameTracking(FBTrackingEvent.Crop_Share);
        GameActions.setContext(ContextType.SHARE_CROP_UNLOCKED, {
            field: this.field,
        });

        this.updateHarvestTimer();
    }
    protected onClose(): void {
        super.onClose();
        this.updateHarvestTimer();
    }
    protected updateHarvestTimer(): void {
        TimerActions.updateTimer({
            timer: TimerType.HARVEST,
            time: null,
        });
        Game.scene.getActiveScene().emit('TransitionCrops');
    }

    protected cropIconParams: IImageData = {
        name: 'crop_icon',
        cacheKey: 'wheat_5',
        layout: {
            anchors: [0.5, 0.5, 0.5, 0.5],
            pivot: [0.5, 0.5],
            offsets: [0, 0, 0, 0],
            scale: [1, 1],
            constrains: {
                widthMax: 219,
                widthMin: 219,
                heightMax: 219,
                heightMin: 219,
            },
        },
    };
    protected cropIconBgParams: IImageData = {
        name: 'crop_icon_background',
        cacheKey: 'crop_bg_large',
        layout: {
            anchors: [0.5, 0.46, 0.5, 0.46],
            pivot: [0.5, 0.5],
            offsets: [0, 0, 0, 0],
            scale: [1, 1],
        },
    };
    protected textParams: any = {
        text: {
            name: 'field_unlocked_txt',
            textLocalizationKey: 'fieldUnlocked',
            style: {
                font: 'FrancoisOne',
                fill: '#f4f5c9',
                fontSize: '24px',
            },
            layout: {
                anchors: [0.5, 0.45, 0.5, 0.45],
                offsets: [0, 0, 0, 0],
                pivot: [0.5, 0.5],
            },
        },
        bg: {
            name: 'field_unlocked_txt_bg',
            layout: {
                anchors: [0.5, 0.72, 0.5, 0.72],
                offsets: [0, 0, 0, 0],
                pivot: [0.5, 0.5],
            },
            cacheKey: 'text_bg_long_green',
        },
    };

    private configureShareButtonForNative() {
        // On native platforms we omit the share button and center the continue button.
        this.continueBtnParams.background.layout.offsets = [0, 0, 0, 0];
    }
}
